import { ReactNode, useEffect } from "react";

const BADGE_VARIANTS = {
  brand: "bg-brand/10 text-brand",
  success: "bg-success/15 text-success-content dark:text-success",
  knowledge: "bg-knowledge/10 text-knowledge",
  waiting: "bg-waiting/15 text-waiting-content dark:text-waiting",
  risk: "bg-risk/10 text-risk",
  heritage: "bg-heritage/10 text-heritage",
  neutral: "bg-border/60 text-text-muted",
} as const;

export type BadgeVariant = keyof typeof BADGE_VARIANTS;

export function Badge({
  variant = "neutral",
  icon,
  children,
}: {
  variant?: BadgeVariant;
  icon?: ReactNode;
  children: ReactNode;
}) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${BADGE_VARIANTS[variant]}`}>
      {icon}
      {children}
    </span>
  );
}

export function Card({ className = "", children }: { className?: string; children: ReactNode }) {
  return <div className={`rounded-xl border border-border bg-surface ${className}`}>{children}</div>;
}

const BUTTON_VARIANTS = {
  primary: "bg-brand text-brand-content hover:opacity-90",
  secondary: "border border-border text-text hover:bg-border/30",
  danger: "border border-risk/40 text-risk hover:bg-risk/10",
} as const;

export function Button({
  variant = "primary",
  className = "",
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: keyof typeof BUTTON_VARIANTS }) {
  return (
    <button
      className={`rounded-lg px-4 py-2 text-sm font-medium transition-opacity disabled:opacity-50 ${BUTTON_VARIANTS[variant]} ${className}`}
      {...props}
    />
  );
}

export function Modal({ onClose, children, wide = false }: { onClose: () => void; children: ReactNode; wide?: boolean }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/50 p-4" onClick={onClose}>
      <div
        className={`max-h-[90vh] w-full ${wide ? "max-w-lg" : "max-w-sm"} overflow-y-auto rounded-2xl bg-surface shadow-xl`}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}
